import type { Avatar } from '@sokdaq/protocol';
import { platform } from '../platform/bridge.js';

/**
 * 재실행 사이에 살아남아야 하는 값들.
 *
 * 데스크톱에서는 OS 키체인으로 암호화되고, 웹에서는 localStorage에 담긴다.
 * 어느 쪽이든 잃어도 치명적이지 않다 — 닉네임을 다시 입력하고 들어오면 된다.
 */

const KEY_IDENTITY = 'sokdaq.identity';
const KEY_TOKENS = 'sokdaq.tokens';
const KEY_LAST_ROOM = 'sokdaq.lastRoom';

export interface Identity {
  readonly nickname: string;
  readonly avatar: Avatar;
}

async function readJson<T>(key: string): Promise<T | null> {
  const raw = await platform.secureStore.get(key);
  if (raw === null) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    // 형식이 깨졌으면 없는 셈 친다. 다음 저장 때 덮어써진다.
    return null;
  }
}

export function loadIdentity(): Promise<Identity | null> {
  return readJson<Identity>(KEY_IDENTITY);
}

export function saveIdentity(identity: Identity): Promise<void> {
  return platform.secureStore.set(KEY_IDENTITY, JSON.stringify(identity));
}

/**
 * 방별 재접속 토큰.
 *
 * 초대코드를 열쇠로 쓴다. 방장이 코드를 회전시키면 새 신원으로
 * 들어가게 되는데, 그건 회전의 의도에 어긋나지 않는다.
 */
export async function loadMemberToken(code: string): Promise<string | null> {
  const tokens = await readJson<Record<string, string>>(KEY_TOKENS);
  return tokens?.[code] ?? null;
}

export async function saveMemberToken(code: string, token: string): Promise<void> {
  const tokens = (await readJson<Record<string, string>>(KEY_TOKENS)) ?? {};
  tokens[code] = token;
  await platform.secureStore.set(KEY_TOKENS, JSON.stringify(tokens));
}

/**
 * 토큰을 새 코드 아래로 옮긴다. 초대코드가 회전했을 때 쓴다.
 *
 * 옮기지 않으면 다음 재접속이 토큰 없이 나가고, 서버는 새 사용자로 보고
 * 새 userId를 준다 — 방에 자기 자신이 둘로 보이게 된다.
 */
export async function rekeyMemberToken(oldCode: string, newCode: string): Promise<void> {
  const tokens = (await readJson<Record<string, string>>(KEY_TOKENS)) ?? {};
  const token = tokens[oldCode];
  if (token === undefined) return;

  delete tokens[oldCode];
  tokens[newCode] = token;
  await platform.secureStore.set(KEY_TOKENS, JSON.stringify(tokens));
}

export function loadLastRoom(): Promise<string | null> {
  return platform.secureStore.get(KEY_LAST_ROOM);
}

export function saveLastRoom(code: string): Promise<void> {
  return platform.secureStore.set(KEY_LAST_ROOM, code);
}

export function clearLastRoom(): Promise<void> {
  return platform.secureStore.remove(KEY_LAST_ROOM);
}
